import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Avatar } from "antd";
import {
  CustomerServiceOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import paths from "../../routes/paths";
import { brandName } from "../../pages/care/careData";
import WorkspacePicker from "../../workspaces/WorkspacePicker";
import { OLIVEEB_COLORS } from "../../workspaces/colors";
import "../layout/layout.css";
import { websiteOwnerTypeForWorkspace } from "pages/websiteUtils";
import { careService } from "../../services/careService";
import { readable } from "../../workspaces/workspaceUtils";
import useWorkspaceSubscriptionFeatures from "../../hooks/useWorkspaceSubscriptionFeatures";
import {
  hasSubscriptionFeature,
  SUBSCRIPTION_FEATURE_KEYS,
} from "../../workspaces/subscriptionFeatures";
import CompliancePolicyToolsModal from "./CompliancePolicyToolsModal";
import { CARE_VIEW_INCIDENTS, hasWorkspacePrivilege } from "../../utils/carePrivileges";
// import { websiteOwnerTypeForWorkspace } from "pages/websiteUtils";

export const CareShellActiveContext = createContext(false);

const cleanText = (value) => (typeof value === "string" ? value.trim() : "");
const currentUserId = (user = {}) =>
  user?.id || user?.userId || user?.uid || "";
const normalizeRoleToken = (role) =>
  String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
const careProfileIdFor = (profile = {}) =>
  profile?.id || profile?.careProfileId || profile?.profileId || "";
const careProfileIsComplete = (profile = {}) =>
  ["COMPLETE", "COMPLETED", "ACTIVE", "APPROVED"].includes(
    String(profile?.onboardingStatus || profile?.status || "").toUpperCase(),
  );
const SIDEBAR_CARE_PROFILE_CACHE_TTL_MS = 5 * 60 * 1000;
const sidebarCareProfileCache = new Map();
const pendingSidebarCareProfileLoads = new Map();
const sidebarCareProfileCacheKey = ({ actorId, workspaceId }) =>
  `${actorId || "anonymous"}:${workspaceId || "none"}`;
const freshSidebarCareProfileCacheEntry = (entry, now = Date.now()) =>
  entry && now - entry.fetchedAt < SIDEBAR_CARE_PROFILE_CACHE_TTL_MS;
const normalizeCareProfile = (result) => {
  if (Array.isArray(result)) return result[0] || null;
  if (Array.isArray(result?.content)) return result.content[0] || null;
  if (Array.isArray(result?.data)) return result.data[0] || null;
  return result || null;
};

const workspaceDisplayName = (workspace) =>
  cleanText(workspace?.name) ||
  cleanText(workspace?.displayName) ||
  cleanText(workspace?.organizationName) ||
  cleanText(workspace?.businessName) ||
  "Current workspace";

const workspaceDisplayType = (workspace) =>
  readable(
    workspace?.facilityType ||
      workspace?.careFacilityType ||
      workspace?.organizationType ||
      workspace?.accountType ||
      workspace?.product,
    "Workspace",
  );

const userDisplayName = (user) =>
  cleanText(user?.displayName) ||
  [
    user?.preferredFirstName || user?.firstName,
    user?.preferredLastName || user?.lastName,
  ]
    .map(cleanText)
    .filter(Boolean)
    .join(" ") ||
  cleanText(user?.name) ||
  cleanText(user?.username) ||
  cleanText(user?.email) ||
  "Care Provider";

const userRoleLabel = (user) =>
  cleanText(user?.roleLabel) ||
  cleanText(user?.profession) ||
  cleanText(user?.jobTitle) ||
  cleanText(user?.title) ||
  cleanText(user?.primaryRole) ||
  cleanText(user?.role) ||
  "Care Provider";

const userInitials = (name) => {
  const parts = cleanText(name).split(/\s+/).filter(Boolean);
  if (!parts.length) return "CP";
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
};

export function Logo() {
  return (
    <Link className="brand" to="/">
      <img className="brand-logo" src="/oliveeb/ocarely.png" alt={brandName} />
    </Link>
  );
}

export function Footer({ publicLinks = false } = {}) {
  const year = new Date().getFullYear();

  return (
    <footer className="site-footer care-site-footer">
      <div>
        <Logo />
        <p>
          Resident care operations, staff schedules, documents, billing,
          websites, and AI-assisted workflows in one place.
        </p>
        <small>© {year} OCarely. All rights reserved.</small>
      </div>
      <div>
        <strong>Resources</strong>
        <Link to="/get-started">Get Started</Link>
        {publicLinks ? (
          <Link to="/pricing">Pricing</Link>
        ) : (
          <Link to="/support">Support</Link>
        )}
        <a href="/privacy-policy">Privacy Policy</a>
      </div>
      <div>
        <strong>Follow Us</strong>
        <div className="socials">
          <a href="https://www.facebook.com/ocarely" target="_blank" rel="noreferrer" aria-label="Follow OCarely on Facebook">
            <i className="ti ti-brand-facebook" />
          </a>
          <a href="https://www.linkedin.com/company/ocarely" target="_blank" rel="noreferrer" aria-label="Follow OCarely on LinkedIn">
            <i className="ti ti-brand-linkedin" />
          </a>
          <a href="https://www.tiktok.com/@ocarely.com" target="_blank" rel="noreferrer" aria-label="Follow OCarely on TikTok">
            <i className="ti ti-brand-tiktok" />
          </a>
          <a href="https://www.instagram.com/ocarelydotcom/" target="_blank" rel="noreferrer" aria-label="Follow OCarely on Instagram">
            <i className="ti ti-brand-instagram" />
          </a>
          <a href="https://x.com/OCarelyDotCom" target="_blank" rel="noreferrer" aria-label="Follow OCarely on X">
            <i className="ti ti-brand-x" />
          </a>
          <a href="https://www.youtube.com/@OCarely" target="_blank" rel="noreferrer" aria-label="Follow OCarely on YouTube">
            <i className="ti ti-brand-youtube" />
          </a>
        </div>
      </div>
    </footer>
  );
}

export function HeaderUserProfile() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [ddOpen, setDdOpen] = useState(false);
  const menuRef = useRef(null);
  const name = userDisplayName(user);
  const role = userRoleLabel(user);
  const photo = cleanText(
    user?.profilePhoto || user?.avatarUrl || user?.photoUrl || user?.imageUrl,
  );
  const email = cleanText(user?.email) || "Oliveeb account";
  const initials = useMemo(() => userInitials(name), [name]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!menuRef.current || menuRef.current.contains(event.target)) return;
      setDdOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const profileMenuItems = [
    {
      label: "Profile",
      description: "View your Oliveeb identity",
      icon: <UserOutlined />,
      tone: "blue",
      path: paths.profile,
    },
    {
      label: "Settings",
      description: "Tune your workspace experience",
      icon: <SettingOutlined />,
      tone: "yellow",
      path: paths.settings,
    },
    {
      label: "Help",
      description: "Guides, answers, and tips",
      icon: <QuestionCircleOutlined />,
      tone: "blue",
      path: paths.help,
    },
    {
      label: "Support",
      description: "Get help from Oliveeb",
      icon: <CustomerServiceOutlined />,
      tone: "orange",
      path: paths.support,
    },
  ];

  const goToProfileMenuItem = (path) => {
    setDdOpen(false);
    navigate(path);
  };

  return (
    <div className="profile-tools">
      <span className="notification">
        <i className="ti ti-bell" />
        <b>3</b>
      </span>
      <div
        className="care-profile-trigger pm-avw"
        ref={menuRef}
        role="button"
        tabIndex={0}
        aria-label="Open profile menu"
        aria-expanded={ddOpen}
        onClick={(event) => {
          event.stopPropagation();
          setDdOpen((current) => !current);
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          setDdOpen((current) => !current);
        }}
      >
        <div className="pm-av care-profile-avatar">
          <Avatar
            src={photo || undefined}
            style={{
              width: "100%",
              height: "100%",
              borderRadius: "999px",
              background: photo
                ? undefined
                : `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebDarkBlue}, ${OLIVEEB_COLORS.oliveebBlue})`,
              color: OLIVEEB_COLORS.white,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {!photo ? initials : null}
          </Avatar>
          <span className="pm-avon" />
        </div>
        <div className="profile-label">
          <strong>{name}</strong>
          <span>{role}</span>
        </div>
        <i
          className={`ti ti-chevron-down care-profile-chevron${ddOpen ? " is-open" : ""}`}
        />

        {ddOpen && (
          <div className="pm-dd" onClick={(event) => event.stopPropagation()}>
            <div className="pm-ddh">
              <div className="pm-dd-avatar">
                <Avatar
                  src={photo || undefined}
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "999px",
                    background: photo
                      ? undefined
                      : `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebGreen}, ${OLIVEEB_COLORS.oliveebBlue})`,
                    color: OLIVEEB_COLORS.white,
                    fontSize: 16,
                    fontWeight: 900,
                  }}
                >
                  {!photo ? initials : null}
                </Avatar>
              </div>
              <div className="pm-dd-user">
                <div className="pm-dd-kicker">Signed in as</div>
                <div className="pm-ddn">{name}</div>
                <div className="pm-dde">{email}</div>
              </div>
            </div>
            <div className="pm-dd-list">
              {profileMenuItems.map((item) => (
                <div
                  key={item.label}
                  className="pm-ddi pm-dd-action"
                  data-tone={item.tone}
                  onClick={() => goToProfileMenuItem(item.path)}
                >
                  <span className="pm-dd-ico">{item.icon}</span>
                  <span className="pm-dd-copy">
                    <span className="pm-dd-label">{item.label}</span>
                    <span className="pm-dd-desc">{item.description}</span>
                  </span>
                </div>
              ))}
            </div>
            <div
              className="pm-ddi pm-dd-action dng mt-2"
              onClick={() => logout(false, paths.landing)}
            >
              <span className="pm-dd-ico">
                <LogoutOutlined />
              </span>
              <span className="pm-dd-copy">
                <span className="pm-dd-label">Sign out</span>
                <span className="pm-dd-desc">End this browser session</span>
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function SideNav({ collapsed = false, mobileOpen = false, onNavigate, onMobileClose }) {
  const [expandedGroupKey, setExpandedGroupKey] = useState(null);
  const [careProfile, setCareProfile] = useState(null);
  const [workspaceRolesReady, setWorkspaceRolesReady] = useState(false);
  const [toolsModalOpen, setToolsModalOpen] = useState(false);
  const location = useLocation();
  const { user, careRoles = [], privileges = [], refreshCareRoles } = useAuth();
  const { selectedWorkspaceId, selectedWorkspace, isOrganizationWorkspace } =
    useWorkspace();
  const workspaceId = selectedWorkspaceId || "current";
  const workspaceName = workspaceDisplayName(selectedWorkspace);
  const workspaceType = workspaceDisplayType(selectedWorkspace);
  const actorId = currentUserId(user);
  const normalizedCareRoles = careRoles.map(normalizeRoleToken);
  const canViewIncidents = hasWorkspacePrivilege({ privileges, user, workspace: selectedWorkspace }, CARE_VIEW_INCIDENTS);
  const {
    features: subscriptionFeatures,
    loading: subscriptionLoading,
    tier: subscriptionTierName,
  } = useWorkspaceSubscriptionFeatures();
  const hasFeature = (feature) => hasSubscriptionFeature(subscriptionFeatures, feature);
  const hasCareSubscription =
    !subscriptionLoading &&
    ["STANDARD", "PREMIUM"].includes(String(subscriptionTierName || "").toUpperCase());
  const isIndividualWorkspace = Boolean(selectedWorkspace) && !isOrganizationWorkspace;
  const canManageCareAdminFeatures = workspaceRolesReady && (
    normalizedCareRoles.includes("care_owner") || normalizedCareRoles.includes("care_admin")
  );
  const shouldShowSubscriptionUpgradePrompt =
    isOrganizationWorkspace &&
    canManageCareAdminFeatures &&
    !subscriptionLoading &&
    !["STANDARD", "PREMIUM"].includes(String(subscriptionTierName || "").toUpperCase());
  const shouldShowSubscriptionLoader =
    isOrganizationWorkspace &&
    canManageCareAdminFeatures &&
    subscriptionLoading;
  const featureKeys = SUBSCRIPTION_FEATURE_KEYS;
  const closedGroupKey = "__closed__";

  const ownerType = websiteOwnerTypeForWorkspace({
    isOrganizationWorkspace,
    selectedWorkspace,
  });

  const currentWorkspaceDetailsPath = selectedWorkspaceId
    ? paths.workspaceDetails(selectedWorkspaceId)
    : paths.workspaces;
  const menuCareProfileId = careProfileIdFor(careProfile);
  const linkedProfessionalProfileId = careProfile?.professionalProfileId || "";
  const careProfileMenuItem =
    menuCareProfileId && careProfileIsComplete(careProfile)
      ? [
          "My Care Profile",
          "ti-heart-handshake",
          `/staff/${encodeURIComponent(menuCareProfileId)}`,
        ]
      : ["My Onboarding", "ti-clipboard-check", "/care-profile"];

  useEffect(() => {
    let active = true;
    const cacheKey = sidebarCareProfileCacheKey({ actorId, workspaceId: selectedWorkspaceId });
    const cached = sidebarCareProfileCache.get(cacheKey);

    if (freshSidebarCareProfileCacheEntry(cached)) {
      setCareProfile(cached.profile || null);
      return () => {
        active = false;
      };
    }

    if (cached) {
      setCareProfile(cached.profile || null);
    }

    if (!actorId || !selectedWorkspaceId) {
      setCareProfile(null);
      return () => {
        active = false;
      };
    }

    const loadPromise = pendingSidebarCareProfileLoads.get(cacheKey) ||
      careService.careProfiles
        .getMine({
          actorId,
          userId: actorId,
          workspaceId: selectedWorkspaceId,
        })
        .then((result) => {
          const profile = normalizeCareProfile(result);
          sidebarCareProfileCache.set(cacheKey, {
            profile,
            fetchedAt: Date.now(),
          });
          return profile;
        })
        .catch((error) => {
          if (cached) return cached.profile || null;
          throw error;
        })
        .finally(() => {
          pendingSidebarCareProfileLoads.delete(cacheKey);
        });

    pendingSidebarCareProfileLoads.set(cacheKey, loadPromise);
    loadPromise
      .then((result) => {
        if (!active) return;
        setCareProfile(result || null);
      })
      .catch(() => {
        if (!active) return;
        setCareProfile(null);
      });

    return () => {
      active = false;
    };
  }, [actorId, selectedWorkspaceId]);

  useEffect(() => {
    let active = true;
    setWorkspaceRolesReady(false);
    if (!actorId || !refreshCareRoles || !selectedWorkspaceId) {
      return () => {
        active = false;
      };
    }
    refreshCareRoles({
      workspaceId: selectedWorkspaceId,
      wid: selectedWorkspaceId,
    }).finally(() => {
      if (active) setWorkspaceRolesReady(true);
    });
    return () => {
      active = false;
    };
  }, [actorId, refreshCareRoles, selectedWorkspaceId]);

  const hireCareGiverMenuItems = [
    ["Talent Search", "ti-search", "/hire-care-giver/talent-search"],
    ["Job Posts", "ti-file-text", "/hire-care-giver/job-posts"],
    ["Applications", "ti-clipboard-list", "/hire-care-giver/applications"],
    [
      "Interviews & Hirings",
      "ti-calendar-check",
      "/hire-care-giver/interviews",
    ],
    ["Create Job", "ti-plus", "/hire-care-giver/job-posts/new"],
    [
      "Background Checks",
      "ti-shield-check",
      "/hire-care-giver/background-checks",
    ],
    hasFeature(featureKeys.reports)
      ? ["Reports", "ti-chart-bar", "/hire-care-giver/reports"]
      : null,
  ].filter(Boolean);
  const shiftFillerMenuItems = [
    ["Post New Shift", "ti-calendar-plus", "/hire-care-giver/shift-filler/new"],
    ["Posted Shifts", "ti-list-details", "/hire-care-giver/shift-filler/posts"],
  ];
  const generalTaskMenuItems = [
    ["Post New Task", "ti-plus", "/hire-care-giver/general-tasks/new"],
    ["Posted Tasks", "ti-list-details", "/hire-care-giver/general-tasks/posts"],
  ];
  const careMenuItems = [["Dashboard", "ti-home", "/dashboard"]];
  const residentCareMenuItems = [
    hasFeature(featureKeys.medicationManagement)
      ? ["eMAR", "ti-pill", "/resident-medications"]
      : null,
    hasFeature(featureKeys.assessments)
      ? ["Daily Notes", "ti-notes", "/daily-notes"]
      : null,
    hasFeature(featureKeys.residents)
      ? ["Vital Signs", "ti-activity-heartbeat", "/resident-vital-signs"]
      : null,
    hasFeature(featureKeys.carePlans)
      ? ["ADLs", "ti-list-check", "/resident-adls"]
      : null,
    hasFeature(featureKeys.residents)
      ? ["Residents", "ti-users", "/residents"]
      : null,
    hasFeature(featureKeys.assessments)
      ? ["Resident Care Processes", "ti-clipboard-text", "/assessments"]
      : null,
    hasFeature(featureKeys.incidentReporting) && canViewIncidents
      ? ["Incident Reporting", "ti-alert-triangle", "/incidents"]
      : null,
    hasFeature(featureKeys.residents)
      ? ["Resident CheckInOut", "ti-clock-share", "/resident-clock-in-out"]
      : null,
    hasFeature(featureKeys.carePlans)
      ? ["Care Plans", "ti-heart-handshake", "/resident-care-plans"]
      : null,
    hasFeature(featureKeys.medicationManagement)
      ? ["Pharmacies", "ti-building-store", "/pharmacies"]
      : null,
    ["eFax", "ti-printer", "/efax"],
    // hasFeature(featureKeys.rnDelegationWorkflows) ? ["RN Delegation", "ti-stethoscope", "/rn-delegations"] : null,
    // hasFeature(featureKeys.carePlans) ? ["Care Planning", "ti-tools-kitchen-2", "/care-planning"] : null,
    // hasFeature(featureKeys.familyPortal) ? ["Family Portal", "ti-users-group", "/family-portal"] : null,
  ].filter(Boolean);
  const complianceMenuItems = [
    ["Dashboard", "ti-layout-dashboard", "/compliance", true],
    ["Policies", "ti-clipboard-list", "/compliance/inventory"],
    ["Facility Audit Tools", "ti-apps", "/compliance/audit-tools"],
    ["Alerts", "ti-alert-triangle", "/compliance/alerts"],
  ];
  const staffOperationsMenuItems = [
    hasFeature(featureKeys.staffDirectory) ? ["Staff", "ti-users-group", "/staff"] : null,
    hasFeature(featureKeys.schedules) ? ["Schedules", "ti-calendar-clock", "/schedules"] : null,
    ...(canManageCareAdminFeatures && hasFeature(featureKeys.attendance) ? [
      ["Staff ClockInOut", "ti-clock", "/attendance"],
    ] : []),
    ...(canManageCareAdminFeatures && hasFeature(featureKeys.timesheets) ? [
      ["Timesheets", "ti-calendar-time", "/timesheets"],
    ] : []),
    ...(canManageCareAdminFeatures && hasFeature(featureKeys.payroll) ? [
      ["Payroll", "ti-cash", "/payroll"],
    ] : []),
    // hasFeature(featureKeys.weeklyCaregiverHoursTracking) ? ["Hours Operations", "ti-clock-dollar", "/hours-operations"] : null,
  ].filter(Boolean);
  const downloadsMenuItem = hasFeature(featureKeys.documents)
    ? ["Documents", "ti-files", "/downloads"]
    : null;
  const myWorkMenuItems = [
    careProfileMenuItem,
    hasFeature(featureKeys.schedules) ? ["My Schedule", "ti-calendar-clock", "/my-schedule"] : null,
    hasFeature(featureKeys.attendance) ? ["My Clocking", "ti-clock-hour-4", "/my-attendance"] : null,
    hasFeature(featureKeys.timesheets) ? ["My Timesheets", "ti-calendar-time", "/my-timesheet"] : null,
    ["My Payments", "ti-wallet", "/my-payments"],
  ].filter(Boolean);
  const profileMenuItems = [
    ["Account", "ti-user-circle", paths.profile],
    isIndividualWorkspace
      ? ["Professional", "ti-id", "/hire-care-giver/professional-profiles"]
      : null,
    isOrganizationWorkspace && linkedProfessionalProfileId
      ? [
          "Professional Profile",
          "ti-id",
          paths.professionalProfileDetails(linkedProfessionalProfileId),
        ]
      : null,
    isOrganizationWorkspace && !linkedProfessionalProfileId
      ? [
          "Link Professional Profile",
          "ti-link",
          "/care-profile?step=professional",
        ]
      : null,
  ].filter(Boolean);
  const workspaceAdminMenuItems = [
    ["Manage", "ti-settings", currentWorkspaceDetailsPath],
    canManageCareAdminFeatures && selectedWorkspaceId
      ? ["Notifications", "ti-bell-cog", `/workspaces/${selectedWorkspaceId}/notifications`]
      : null,
    ["Invitations", "ti-mail-opened", "/invitations"],
    ...(ownerType === "PORTFOLIO" || !canManageCareAdminFeatures
      ? []
      : hasFeature(featureKeys.website)
        ? [["Website", "ti-world", "/website"]]
        : []),
    ...(canManageCareAdminFeatures
      ? [["Security & Access", "ti-shield-lock", "/access-control"]]
      : []),
  ].filter(Boolean);
  // const communicationSupportMenuItems = [
  //   ["Settings", "ti-settings", "/settings"],
  //   ...(hasFeature(featureKeys.chatInbox) ? [
  //     ["Chat", "ti-messages", "/chat-space"],
  //     ["Inbox", "ti-inbox", "/email-messages"],
  //   ] : []),
  //   ...(hasFeature(featureKeys.secureCareTeamDocumentExchange) ? [
  //     ["Secure Exchange", "ti-mail-lock", "/secure-care-team-exchange"],
  //   ] : []),
  //   ["Help & Support", "ti-headset", "/support"],
  //   ["Share Feedback", "ti-message-report", "/feedback"],
  //   ["Legal and Privacy", "ti-file-certificate", "https://oliveeb.com/privacy_policy.html#policy-sections"],
  // ];
  const paymentPlanMenuItems = [
    ["Wallet", "ti-wallet", `/workspaces/${workspaceId}/payment-methods`],
    ["Charges", "ti-receipt", `/workspaces/${workspaceId}/charges`],
    ["Subscriptions", "ti-repeat", `/workspaces/${workspaceId}/subscriptions`],
    ["Partnerships", "ti-heart-handshake", "/partners"],
  ];
  const isExternalPath = (path) => String(path || "").startsWith("http");
  const isActivePath = (path, exact = false) =>
    !isExternalPath(path) &&
    (location.pathname === path ||
      (!exact && path !== "/" && location.pathname.startsWith(`${path}/`)));
  const shiftFillerMenuActive = location.pathname.startsWith(
    "/hire-care-giver/shift-filler",
  );
  const generalTaskMenuActive = location.pathname.startsWith(
    "/hire-care-giver/general-tasks",
  );
  const hireMenuActive =
    location.pathname.startsWith("/hire-care-giver") &&
    !shiftFillerMenuActive &&
    !generalTaskMenuActive;
  const isGroupActive = (items = []) =>
    items.some((item) => isActivePath(item[2]));
  const toggleGroup = (key, isOpen) => {
    setExpandedGroupKey(isOpen ? closedGroupKey : key);
  };

  useEffect(() => {
    setExpandedGroupKey(null);
  }, [location.pathname]);
  const renderSectionLabel = (label) => (
    <div className="sidebar-section-label" key={`section-${label}`}>
      {label}
    </div>
  );
  const renderMenuItem = ([label, icon, path, exact]) => {
    const external = isExternalPath(path);
    const content = (
      <>
        <i className={`ti ${icon}`} />
        <span className="sidebar-label">{label}</span>
      </>
    );
    return external ? (
      <a
        className=""
        href={path}
        key={label}
        title={label}
        target="_blank"
        rel="noreferrer"
        onClick={onNavigate}
      >
        {content}
      </a>
    ) : (
      <Link
        className={isActivePath(path, exact) ? "active" : ""}
        to={path}
        key={label}
        title={label}
        onClick={onNavigate}
      >
        {content}
      </Link>
    );
  };
  const renderSubMenuItem = ([label, icon, path, exact]) => {
    const external = isExternalPath(path);
    const content = (
      <>
        <i className={`ti ${icon}`} />
        <span>{label}</span>
      </>
    );
    return external ? (
      <a
        className="sidebar-subitem"
        href={path}
        key={label}
        title={label}
        target="_blank"
        rel="noreferrer"
        onClick={onNavigate}
      >
        {content}
      </a>
    ) : (
      <Link
        className={`sidebar-subitem${isActivePath(path, exact) ? " active" : ""}`}
        to={path}
        key={label}
        title={label}
        onClick={onNavigate}
      >
        {content}
      </Link>
    );
  };
  const renderMenuGroup = ({
    key,
    label,
    icon,
    items,
    activeOverride = false,
    children,
  }) => {
    if (!items?.length && !children) return null;
    const active = activeOverride || isGroupActive(items);
    const isOpen = expandedGroupKey === closedGroupKey
      ? false
      : expandedGroupKey
        ? expandedGroupKey === key
        : active;
    return (
      <div className="sidebar-menu-group" key={key}>
        <button
          type="button"
          className={`sidebar-group-toggle${isOpen ? " is-open" : ""}${active ? " active" : ""}`}
          onClick={() => toggleGroup(key, isOpen)}
          title={label}
          aria-expanded={isOpen}
        >
          <i className={`ti ${icon}`} />
          <span className="sidebar-label">{label}</span>
          <i className="ti ti-chevron-down sidebar-chevron" />
        </button>
        {isOpen && !collapsed && (
          <div className="sidebar-submenu">
            {items?.map(renderSubMenuItem)}
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`sidebar${collapsed ? " is-collapsed" : ""}${mobileOpen ? " mobile-open" : ""}`}
      aria-hidden={!mobileOpen ? undefined : false}
    >
      <button type="button" className="sidebar-mobile-close" onClick={onMobileClose} aria-label="Close menu">
        <i className="ti ti-x" />
      </button>
      <Logo />
      <nav>
        {hasCareSubscription ? (
          <>
            {renderSectionLabel("Care")}
            {careMenuItems.map(renderMenuItem)}
            {isOrganizationWorkspace ? renderMenuGroup({
              key: "residentCare",
              label: "Care Operations",
              icon: "ti-users",
              items: residentCareMenuItems,
            }) : null}
            {isOrganizationWorkspace ? renderMenuGroup({
              key: "staffOperations",
              label: "Staff Operations",
              icon: "ti-calendar-clock",
              items: staffOperationsMenuItems,
            }) : null}
            {isOrganizationWorkspace ? renderMenuGroup({
              key: "compliance",
              label: "Compliance",
              icon: "ti-shield-check",
              items: complianceMenuItems,
            }) : null}
            {downloadsMenuItem ? renderMenuItem(downloadsMenuItem) : null}
          </>
        ) : null}

        {renderSectionLabel("Accounts")}
        {isOrganizationWorkspace
          ? renderMenuGroup({
              key: "myWork",
              label: "My Work",
              icon: "ti-user-circle",
              items: myWorkMenuItems,
            })
          : null}
        {renderMenuGroup({
          key: "profiles",
          label: "Profiles",
          icon: "ti-id",
          items: profileMenuItems,
        })}
        {renderMenuGroup({
          key: "workspaceAdmin",
          label: "Workspace",
          icon: "ti-building",
          items: workspaceAdminMenuItems,
        })}

        {canManageCareAdminFeatures && hasFeature(featureKeys.hiring) ? (
          <>
            {renderSectionLabel("Hiring")}
            {renderMenuGroup({
              key: "shiftFiller",
              label: "Shift Filler",
              icon: "ti-heart-handshake",
              items: shiftFillerMenuItems,
              activeOverride: shiftFillerMenuActive,
            })}
            {renderMenuGroup({
              key: "generalTask",
              label: "General Task",
              icon: "ti-checkbox",
              items: generalTaskMenuItems,
              activeOverride: generalTaskMenuActive,
            })}
            {renderMenuGroup({
              key: "hiring",
              label: "Hire Care Giver",
              icon: "ti-user-search",
              items: hireCareGiverMenuItems,
              activeOverride: hireMenuActive,
            })}
          </>
        ) : null}
        {shouldShowSubscriptionLoader && !collapsed ? (
          <div className="sidebar-upgrade-card sidebar-upgrade-card--loading" aria-label="Loading subscription">
            <i className="ti ti-loader-2" style={{ animation: "spin 1s linear infinite" }} />
            <span>
              <strong>Loading subscription</strong>
              <small>Checking workspace access...</small>
            </span>
          </div>
        ) : null}
        {shouldShowSubscriptionUpgradePrompt && !collapsed ? (
          <Link
            className="sidebar-upgrade-card"
            to="/workspaces/current/subscriptions"
            onClick={onNavigate}
          >
            <i className="ti ti-lock" />
            <span>
              <strong>Upgrade subscription</strong>
              <small>Standard or Premium unlocks care tools.</small>
            </span>
          </Link>
        ) : null}

        {/* {renderSectionLabel("General")}
        {renderMenuGroup({
          key: "communicationSupport",
          label: "Communication & Support",
          icon: "ti-messages",
          items: communicationSupportMenuItems,
        })} */}

        {canManageCareAdminFeatures ? (
          <>
            {renderSectionLabel("Payments & Plans")}
            {renderMenuGroup({
              key: "paymentsPlans",
              label: "Payments & Plans",
              icon: "ti-wallet",
              items: paymentPlanMenuItems,
            })}
          </>
        ) : null}
      </nav>
      <Link
        className="facility-card"
        to={selectedWorkspaceId ? paths.workspaces : paths.pmDashboard}
        onClick={onNavigate}
      >
        <span className="soft-icon">
          <i className="ti ti-building-hospital" />
        </span>
        <div>
          <strong>{workspaceName}</strong>
          <small>{workspaceType}</small>
        </div>
        <i className="ti ti-chevron-down" />
      </Link>
      <div className="help-card">
        <strong>Need help?</strong>
        <p>Visit our Help Center or contact support.</p>
        <Link to={paths.help}>
          Help Center <i className="ti ti-arrow-right" />
        </Link>
      </div>

    </aside>
  );
}

export function CareShell({
  children,
  className = "dashboard-content",
  embedded = false,
}) {
  const shellAlreadyActive = useContext(CareShellActiveContext);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [mobileMenuOpen]);

  if (embedded || shellAlreadyActive) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className={`app-shell ${sidebarCollapsed ? "sidebar-collapsed" : ""}${mobileMenuOpen ? " mobile-menu-open" : ""}`}>
      <button
        type="button"
        className="sidebar-mobile-backdrop"
        aria-label="Close menu"
        onClick={() => setMobileMenuOpen(false)}
      />
      <SideNav
        collapsed={sidebarCollapsed}
        mobileOpen={mobileMenuOpen}
        onNavigate={() => setMobileMenuOpen(false)}
        onMobileClose={() => setMobileMenuOpen(false)}
      />
      <main className="dashboard-main">
        <div className="dashboard-top">
          <div className="page-nav-actions">
            <button
              className="icon-button"
              aria-label="Open menu"
              aria-expanded={mobileMenuOpen}
              onClick={() => {
                if (window.matchMedia("(max-width: 820px)").matches) {
                  setMobileMenuOpen((current) => !current);
                  return;
                }
                setSidebarCollapsed((current) => !current);
              }}
            >
              <i className="ti ti-menu-2" />
            </button>
            <button
              className="icon-button"
              aria-label="Go back"
              onClick={() => navigate(-1)}
            >
              <i className="ti ti-arrow-left" />
            </button>
            <button
              className="icon-button"
              aria-label="Refresh page"
              onClick={() => window.location.reload()}
            >
              <i className="ti ti-refresh" />
            </button>
            <Link className="care-top-logo" to="/home" aria-label="OCarely home">
              <img src="/oliveeb/ocarely.png" alt="OCarely" />
            </Link>
          </div>
          <div className="care-header-workspace">
            <WorkspacePicker />
          </div>
          <HeaderUserProfile />
        </div>
        <div className={className}>
          {children}
          <Footer />
        </div>
      </main>
    </div>
  );
}

export function Panel({ title, action, actionNode, children, className = "" }) {
  const hasHeader = Boolean(title || action || actionNode);
  return (
    <section className={`panel${className ? ` ${className}` : ""}`}>
      {hasHeader ? (
        <div className="panel-head">
          {title ? <h2>{title}</h2> : null}
          {actionNode}
          {action && <a href="#view">{action}</a>}
        </div>
      ) : null}
      {children}
    </section>
  );
}
